import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLazyGetExportStatusQuery } from "../slices/exportReportSlice";
import { triggerDownload } from "../utils/helpers";
import {
    addExportJob,
    updateExportJob,
    setShowExportToast,
} from "../slices/highVolumeSlice";

const ExportStatusListener = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { exportJobs } = useSelector((state) => state.highVolume);
    const [getExportStatus, { data: exportStatusData }] =
        useLazyGetExportStatusQuery();

    const hasFetchedRef = useRef(false);
    const exportJobsRef = useRef([]);
    const processedJobsRef = useRef(new Set());
    const downloadQueueRef = useRef([]);
    const isDownloadingRef = useRef(false);

    const processDownloadQueue = () => {
        if (isDownloadingRef.current) return;

        const nextDownload = downloadQueueRef.current.shift();
        if (!nextDownload) return;

        isDownloadingRef.current = true;
        triggerDownload(nextDownload.url, nextDownload.fileName);

        setTimeout(() => {
            isDownloadingRef.current = false;
            processDownloadQueue();
        }, 800);
    };

    const enqueueDownload = (url, fileName) => {
        if (!url) return;
        downloadQueueRef.current.push({ url, fileName });
        processDownloadQueue();
    };

    const handleExportStatusResponse = (result) => {
        if (!Array.isArray(result)) return;

        let shouldShowToast = false;

        result.forEach((apiJob) => {
            const status = apiJob.status?.toString().trim().toLowerCase();
            const isCompleted = status === "completed";
            const isFailed = status === "failed";

            if (!isCompleted && !isFailed) return;

            const jobId = apiJob.jobId;
            if (!jobId || processedJobsRef.current.has(jobId)) return;

            const jobStatus = isCompleted ? "success" : "failed";
            const fileName = `${apiJob.reportName}.xlsx`;

            const payload = {
                jobId,
                status: jobStatus,
                fileName,
                ...(isCompleted && { downloadUrl: apiJob.downloadUrl }),
            };

            const existingJob = exportJobsRef.current?.find(
                (job) => job.jobId === jobId
            );

            if (existingJob) {
                dispatch(updateExportJob(payload));
            } else {
                dispatch(addExportJob(payload));
            }

            shouldShowToast = true;
            processedJobsRef.current.add(jobId);

            if (isCompleted && apiJob.downloadUrl) {
                enqueueDownload(apiJob.downloadUrl, fileName);
            }
        });

        if (shouldShowToast) {
            dispatch(setShowExportToast(true));
        }
    };

    useEffect(() => {
        if (!user?.userEmail || hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        getExportStatus({ email: user.userEmail })
            .unwrap()
            .then(handleExportStatusResponse)
            .catch(() => {});
    }, [getExportStatus, user?.userEmail]);

    useEffect(() => {
        exportJobsRef.current = exportJobs || [];
    }, [exportJobs]);

    useEffect(() => {
        handleExportStatusResponse(exportStatusData);
    }, [exportStatusData]);

    return null;
};

export default ExportStatusListener;
